import { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import axios from "axios";
import { captureException } from "@sentry/nextjs";
import { parseISO, format } from "date-fns";
import { ClockIcon } from "@heroicons/react/24/outline";
import Text from "@/components/base/text";
import Checkbox from "@/components/checkbox";
import { sleep } from "@/utils/time";
import LoadingTransition from "./loading-transition";

const CommodityCheckResult = dynamic(() => import("./commodity-check-result"));
const SuccessReview = dynamic(() => import("./success-page"));

const flattenItemsFromPackages = (packages) => {
  return (
    packages?.reduce((acc, curPackage) => {
      const { items } = curPackage;
      return [...acc, ...items];
    }, []) || []
  );
};

export const performCommodityCheck = async (
  formData,
  {
    noCache = false,
    skipRecipientCheck = false,
    isStandaloneCheck = false,
  } = {}
) => {
  const {
    recipientCountry,
    recipientName = null,
    purpose,
    packages,
    recipientEmail = null,
    recipientCallCode = null,
    recipientMobileNo = null,
    senderEmail,
  } = formData;

  let recipientPhone = null;
  if (recipientCallCode && recipientMobileNo) {
    recipientPhone = recipientCallCode
      .split("/")[1]
      .concat(" ", recipientMobileNo);
  }
  try {
    const resp = await axios.post("/api/ai/shipment-form/commodity", {
      noCache,
      countryCode: recipientCountry,
      recipient: recipientName,
      recipientEmail,
      recipientPhone,
      purpose,
      senderEmail,
      skipRecipientCheck,
      isStandaloneCheck,
      // Unpack the items in each package into a single list.
      // as the endpoint accepts a list of items.
      items: flattenItemsFromPackages(packages),
    });
    const responseData = resp.data;
    return responseData;
  } catch (error) {
    console.error(error);
    captureException(error);
  }

  return null;
};

export default function AIReview({ formData, setIsError, setFormData }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasPassedCommodityCheck, setHasPassedCommodityCheck] = useState(false);
  const [isAIResponseValid, setIsAIResponseValid] = useState(true);
  const [hasActionWarnings, setHasActionWarnings] = useState(true);
  const isFirstRender = useRef(true);

  // `data` may be null if user has exhausted the attempts,
  // or commodity check has not been performed.
  const { retries = 0, expiresAt, data: commodityData } = result || {};

  useEffect(() => {
    const getMaxRetries = async () => {
      // Disable the Next button until the user agrees to the terms.
      setIsError(true);

      setLoading(true);
      await sleep(500);
      const resp = await performCommodityCheck(formData, {
        noCache: true,
      });
      setLoading(false);
      setResult(resp);

      isFirstRender.current = false;

      const { data: commodityData } = resp || {};
      if (!commodityData) return;

      const { isCategoriesResponseValid, documents: commodityDocuments } =
        commodityData;

      // Flat out block "Choose Dates" button if there are any "For Your Action" warnings
      setHasActionWarnings(
        commodityDocuments.some((itemDocs) => {
          return itemDocs.some(
            (doc) => doc.messageType.toLowerCase() === "action"
          );
        })
      );
      setIsAIResponseValid(isCategoriesResponseValid);

      if (commodityDocuments.every((itemDocs) => itemDocs.length === 0)) {
        // User doesn not need to acknowledge if the comms check passes.
        setIsError(false);
        setHasPassedCommodityCheck(true);
        return;
      }

      // If OpenAI returns invalid response
      if (!isCategoriesResponseValid) {
        setIsError(false);
        setHasPassedCommodityCheck(false);
        return;
      }

      setIsError(true);
      setHasPassedCommodityCheck(false);
      setFormData((prevForm) => ({
        ...prevForm,
        commodityCheckLogId: commodityData.commodityCheckLogId,
      }));
    };

    getMaxRetries();
  }, []);

  return (
    <div className="w-full max-w-3xl mx-auto rounded-lg">
      <div className="rounded-lg bg-white px-10 py-8 shadow">
        {hasPassedCommodityCheck ? (
          <SuccessReview />
        ) : (
          <>
            <Text weight="semibold" size="lg" color="darkPurple" align="center">
              Customs Clearance Check
            </Text>

            <div className="mt-10 mb-10 flex justify-center">
              <LoadingTransition loading={loading} hasData={!!commodityData}>
                <CommodityCheckResult
                  data={commodityData}
                  items={flattenItemsFromPackages(formData.packages)}
                />
              </LoadingTransition>
            </div>
            {!loading && expiresAt && retries < 1 && (
              <>
                <Text
                  weight="normal"
                  color="primary"
                  align="center"
                  className="mt-1"
                >
                  Whoa there! You&apos;ve made quite a few checks. <br />
                  Please take a short break and come back at{" "}
                  <span className="inline-block -mb-[2px]">
                    <ClockIcon
                      className="h-[14px] w-[14px]"
                      aria-hidden="true"
                    />
                  </span>{" "}
                  {format(parseISO(expiresAt), "HH:mm")}
                </Text>
              </>
            )}
          </>
        )}
      </div>

      {isAIResponseValid && !hasPassedCommodityCheck && !hasActionWarnings && (
        <div className="px-5 md:px-10 mt-4 mb-4 font-semibold text-sm flex justify-center">
          <Checkbox
            id="user-accept-shipping-risks"
            label="I acknowledge the risks associated with shipping these items"
            containerClassName="mt-1 flex items-center justify-center"
            onChange={(e) => {
              setFormData((prevForm) => ({
                ...prevForm,
                userAcceptCommodityCheck: e.target.checked,
              }));
              setIsError(!e.target.checked);
            }}
          />
        </div>
      )}
      <div className="px-5 md:px-10 mt-8 flex justify-center">
        <Text size="vsm" color="lightGreyOnGreyBg" align="start">
          <b>Heads Up</b> – This AI check is free guidance based on courier
          rules and our shipping experience. It isn&apos;t licensed customs
          brokerage and clearance isn&apos;t guaranteed—customs has the final
          say. By clicking &quot;Proceed & Accept&quot;, you agree that JustShip
          isn&apos;t liable for customs outcomes, duties, or any costs arising
          from return, disposal, storage, or redelivery of your shipment, and
          you will be responsible for paying any such charges.
        </Text>
      </div>
    </div>
  );
}
